import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavLink } from 'react-router-dom';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const SidebarNav = ({
  widthExpanded = 292,
  widthCollapsed = 76,
  sections = [],
  brand = 'Workmate',
  workspaceLabel,
  bottom,
  bell,
  mobileOpen = false,
  onMobileClose,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const effectiveCollapsed = isMobile ? false : collapsed;

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: (s.items || []).filter((i) => (i.label || '').toLowerCase().includes(q)),
      }))
      .filter((s) => (s.items || []).length > 0);
  }, [query, sections]);

  const sidebarContent = (
    <Box
      sx={{
        width: effectiveCollapsed ? widthCollapsed : widthExpanded,
        flexShrink: 0,
        height: '100vh',
        position: isMobile ? 'relative' : 'sticky',
        top: 0,
        borderRight: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!isMobile ? (
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 950,
                letterSpacing: -0.4,
                fontSize: effectiveCollapsed ? 16 : 18,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {brand}
            </Typography>
            {!effectiveCollapsed && workspaceLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {workspaceLabel}
              </Typography>
            ) : null}
          </Box>
          {bell ? <Box sx={{ flexShrink: 0 }}>{bell}</Box> : null}
          <Tooltip title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <IconButton size="small" onClick={() => setCollapsed(v => !v)}>
              {effectiveCollapsed ? <KeyboardDoubleArrowRightRoundedIcon /> : <KeyboardDoubleArrowLeftRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {!effectiveCollapsed ? (
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            placeholder="Search navigation…"
            fullWidth
            sx={{ mt: 1.25 }}
            slotProps={{
              input: {
                startAdornment: <SearchRoundedIcon fontSize="small" style={{ marginRight: 8, opacity: 0.7 }} />,
              },
            }}
          />
        ) : null}
      </Box>
      ) : (
      <Box sx={{ px: 1.25, pt: 1.25, pb: 1 }}>
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          placeholder="Search navigation…"
          fullWidth
          slotProps={{
            input: {
              startAdornment: <SearchRoundedIcon fontSize="small" style={{ marginRight: 8, opacity: 0.7 }} />,
            },
          }}
        />
      </Box>
      )}

      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {filteredSections.map((section) => (
          <NavSection
            key={section.id}
            section={section}
            collapsed={effectiveCollapsed}
            onNavigate={isMobile ? onMobileClose : undefined}
          />
        ))}
      </Box>

      <Divider />
      <Box sx={{ p: 1.25 }}>{bottom}</Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: widthExpanded,
            maxWidth: '85vw',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return sidebarContent;
};

const NavSection = ({ section, collapsed, onNavigate }) => {
  const [open, setOpen] = useState(Boolean(section.defaultOpen));

  const hasHeader = Boolean(section.label);
  const canCollapse = !collapsed && hasHeader && section.collapsible;

  return (
    <Box sx={{ mb: 1 }}>
      {hasHeader ? (
        <Box
          onClick={canCollapse ? () => setOpen(v => !v) : undefined}
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: canCollapse ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          {!collapsed ? (
            <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 1, opacity: 0.7 }}>
              {section.label}
            </Typography>
          ) : (
            <Box sx={{ height: 12 }} />
          )}

          {canCollapse ? (open ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />) : null}
        </Box>
      ) : null}

      <Collapse in={collapsed ? true : open} timeout="auto" unmountOnExit={!collapsed}>
        <List dense disablePadding>
          {(section.items || []).map((item) => (
            <SidebarNavItem key={item.id || item.to || item.label} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

const SidebarNavItem = ({ item, collapsed, depth = 0, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = item.items && item.items.length > 0;

  const content = (
    <>
      <ListItemIcon sx={{ minWidth: depth > 0 ? 30 : 38 }}>
        {item.icon}
      </ListItemIcon>
      {!collapsed ? <ListItemText primary={item.label} slotProps={{ primary: { fontSize: depth > 0 ? 13 : 13.5, fontWeight: depth > 0 ? 500 : 700 } }} /> : null}
      {!collapsed && hasChildren ? (open ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />) : null}
    </>
  );

  if (hasChildren) {
    return (
      <Box>
        <ListItemButton
          onClick={() => {
            setOpen(!open);
            if (item.to && onNavigate) onNavigate();
          }}
          component={item.to ? NavLink : 'div'}
          to={item.to}
          end={item.to ? Boolean(item.end) : undefined}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.25,
            px: 1,
            py: 0.75,
            pl: depth > 0 ? depth * 2 + 1 : 1,
            '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
            ...(item.to ? {
              '&.active': {
                bgcolor: 'rgba(15, 23, 42, 0.06)',
                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.09)' },
              }
            } : {})
          }}
        >
          {content}
        </ListItemButton>
        <Collapse in={collapsed ? true : open} timeout="auto" unmountOnExit={!collapsed}>
          <List dense disablePadding>
            {item.items.map((subItem) => (
              <SidebarNavItem key={subItem.id || subItem.to || subItem.label} item={subItem} collapsed={collapsed} depth={depth + 1} onNavigate={onNavigate} />
            ))}
          </List>
        </Collapse>
      </Box>
    );
  }

  return (
    <ListItemButton
      key={item.to}
      component={NavLink}
      to={item.to}
      end={Boolean(item.end)}
      onClick={onNavigate}
      sx={{
        borderRadius: 2,
        mx: 1,
        my: 0.25,
        px: 1,
        py: 0.75,
        pl: depth > 0 ? depth * 2 + 1 : 1,
        '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
        '&.active': {
          bgcolor: 'rgba(15, 23, 42, 0.06)',
          '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.09)' },
        },
      }}
    >
      {content}
    </ListItemButton>
  );
};

export default SidebarNav;

